import React, { useState, useEffect, useRef } from 'react';
import { Plus, Download, Trash2, Send, Sparkles, ChevronLeft, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { fetchProblems, fetchSolutions } from '../utils/supabaseHelpers';

const AIAssistant = () => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [problems, setProblems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [problemsWithSolutions, setProblemsWithSolutions] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const messagesEndRef = useRef(null);

  // Get API key from environment variable
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const hasApiKey = apiKey && apiKey.trim() !== '' && apiKey !== 'your_actual_gemini_api_key_here';

  // Generate dynamic suggested questions based on real problem data
  const getSuggestedQuestions = () => {
    if (problems.length === 0) {
      return [
        'How can I help you today?',
        'What would you like to know?',
        'Ask me about your R&D problems',
        'I can help analyze your data'
      ];
    }

    const domains = [...new Set(problems.map(p => p.domain))];
    const priorities = [...new Set(problems.map(p => p.priority))];
    const hasUnresolved = problems.some(p => p.status !== 'completed');

    const questions = [];

    if (domains.includes('mechanical')) {
      questions.push('Show me mechanical problems');
    }
    if (domains.includes('software')) {
      questions.push('What software issues do we have?');
    }
    if (domains.includes('electronics')) {
      questions.push('List electronics domain problems');
    }
    if (priorities.includes('critical')) {
      questions.push('Show me critical priority problems');
    }
    if (hasUnresolved) {
      questions.push('What problems are still unresolved?');
    }
    questions.push('What are the recent trends?');

    return questions.slice(0, 6); // Limit to 6 questions
  };

  useEffect(() => {
    // Load sidebar preference from localStorage
    const savedSidebarState = localStorage.getItem('aiAssistantSidebarCollapsed');
    if (savedSidebarState !== null) {
      setSidebarCollapsed(JSON.parse(savedSidebarState));
    }

    // Check if API key is configured
    if (!hasApiKey) {
      toast.error('Gemini API key not configured. Please check environment variables.');
    }

    // Load real problems from Supabase
    const loadData = async () => {
      try {
        const problemsData = await fetchProblems();

        // Fetch solutions for each problem
        const problemsWithSols = await Promise.all(
          problemsData.map(async (problem) => {
            try {
              const solutions = await fetchSolutions(problem.id);
              return {
                ...problem,
                solutions: solutions || []
              };
            } catch (error) {
              console.error(`Failed to load solutions for problem ${problem.id}:`, error);
              return {
                ...problem,
                solutions: []
              };
            }
          })
        );

        setProblemsWithSolutions(problemsWithSols);
        setProblems(problemsData || []);
        setDocuments([]); // Set empty for now since documents aren't working yet
        console.log('Loaded problems with solutions:', problemsWithSols);
      } catch (error) {
        console.error('Error loading data for AI:', error);
        setProblems([]);
        setDocuments([]);
        setProblemsWithSolutions([]);
        toast.error('Failed to load problems for AI context');
      }
    };

    loadData();

    // Load conversations from localStorage
    const stored = localStorage.getItem('fynex_conversations');
    if (stored) {
      const parsed = JSON.parse(stored);
      setConversations(parsed);
      if (parsed.length > 0) {
        setActiveConversation(parsed[0].id);
        setMessages(parsed[0].messages);
      }
    }
  }, [hasApiKey]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('aiAssistantSidebarCollapsed', JSON.stringify(newState));
  };

  const handleNewChat = () => {
    const newConversation = {
      id: Date.now().toString(),
      title: 'New Conversation',
      messages: [],
      createdAt: new Date().toISOString()
    };
    const updated = [newConversation, ...conversations];
    setConversations(updated);
    setActiveConversation(newConversation.id);
    setMessages([]);
    localStorage.setItem('fynex_conversations', JSON.stringify(updated));
  };

  const handleSelectConversation = (id) => {
    const conversation = conversations.find(c => c.id === id);
    if (conversation) {
      setActiveConversation(id);
      setMessages(conversation.messages);
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all conversations?')) {
      setConversations([]);
      setActiveConversation(null);
      setMessages([]);
      localStorage.removeItem('fynex_conversations');
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear this conversation?')) {
      setMessages([]);
      const updated = conversations.map(c =>
        c.id === activeConversation ? { ...c, messages: [] } : c
      );
      setConversations(updated);
      localStorage.setItem('fynex_conversations', JSON.stringify(updated));
    }
  };

  const handleExport = () => {
    const conversation = conversations.find(c => c.id === activeConversation);
    if (!conversation) return;

    let content = `Fynex AI Assistant - ${conversation.title}\n`;
    content += `Date: ${new Date(conversation.createdAt).toLocaleString()}\n\n`;
    content += '='.repeat(50) + '\n\n';

    conversation.messages.forEach(msg => {
      content += `${msg.role === 'user' ? 'You' : 'Fynex AI'}:\n${msg.content}\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fynex-chat-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateRealDataResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();

    // Handle solution-related queries
    if (lowerMessage.includes('solution')) {
      const problemsWithSols = problemsWithSolutions.filter(p => p.solutions && p.solutions.length > 0);
      if (problemsWithSols.length === 0) {
        return `No problems have solutions recorded yet. You can add solutions through the problem edit form.`;
      }

      const chosenSolutions = problemsWithSolutions.filter(p =>
        p.solutions && p.solutions.some(s => s.is_chosen)
      );

      return `Found ${problemsWithSols.length} problems with solutions (${chosenSolutions.length} have chosen solutions):\n\n${problemsWithSols.slice(0, 3).map(p => {
        const chosen = p.solutions.filter(s => s.is_chosen);
        const total = p.solutions.length;
        return `• ${p.title}\n  Solutions: ${total} total${chosen.length > 0 ? `, ${chosen.length} chosen` : ''}\n  ${chosen.length > 0 ? `Chosen: ${chosen[0].description.substring(0, 60)}...` : `Latest: ${p.solutions[0].description.substring(0, 60)}...`}`;
      }).join('\n\n')}`;
    }

    // Dynamic responses based on actual problem data
    if (lowerMessage.includes('mechanical')) {
      const mechanical = problems.filter(p => p.domain === 'mechanical');
      if (mechanical.length === 0) {
        return `No mechanical problems found in the system.`;
      }
      const unresolved = mechanical.filter(p => p.status !== 'completed');
      return `Found ${mechanical.length} mechanical problems (${unresolved.length} unresolved):\n\n${mechanical.slice(0, 5).map(p => `• ${p.title} - ${p.status} (${p.priority})`).join('\n')}`;
    }

    if (lowerMessage.includes('software')) {
      const software = problems.filter(p => p.domain === 'software');
      if (software.length === 0) {
        return `No software problems found in the system.`;
      }
      return `Found ${software.length} software problems:\n\n${software.slice(0, 5).map(p => `• ${p.title} - ${p.status} (${p.priority})`).join('\n')}`;
    }

    if (lowerMessage.includes('electronics')) {
      const electronics = problems.filter(p => p.domain === 'electronics');
      if (electronics.length === 0) {
        return `No electronics problems found in the system.`;
      }
      return `Found ${electronics.length} electronics problems:\n\n${electronics.slice(0, 5).map(p => `• ${p.title} - ${p.status} (${p.priority})`).join('\n')}`;
    }

    if (lowerMessage.includes('critical')) {
      const critical = problems.filter(p => p.priority === 'critical');
      if (critical.length === 0) {
        return `No critical priority problems found.`;
      }
      return `Found ${critical.length} critical priority problems:\n\n${critical.map(p => `• ${p.title} - ${p.domain} (${p.status})`).join('\n')}`;
    }

    if (lowerMessage.includes('unresolved')) {
      const unresolved = problems.filter(p => p.status !== 'completed');
      if (unresolved.length === 0) {
        return `Great! All problems have been resolved.`;
      }
      return `Found ${unresolved.length} unresolved problems:\n\n${unresolved.slice(0, 5).map(p => `• ${p.title} - ${p.domain} (${p.priority})`).join('\n')}`;
    }

    if (lowerMessage.includes('trend') || lowerMessage.includes('recent')) {
      if (problems.length === 0) {
        return `No problems in the system to analyze trends.`;
      }
      const completed = problems.filter(p => p.status === 'completed').length;
      const inProgress = problems.filter(p => p.status === 'in_progress').length;
      const completionRate = Math.round((completed / problems.length) * 100);

      const domainCounts = problems.reduce((acc, p) => {
        acc[p.domain] = (acc[p.domain] || 0) + 1;
        return acc;
      }, {});

      const topDomain = Object.entries(domainCounts).sort(([, a], [, b]) => b - a)[0];

      return `Problem Analysis:\n\n• Total: ${problems.length}\n• Completed: ${completed} (${completionRate}%)\n• In Progress: ${inProgress}\n• Most common domain: ${topDomain ? topDomain[0] : 'N/A'} (${topDomain ? topDomain[1] : 0} problems)`;
    }

    // Default response
    if (problems.length === 0) {
      return `I'm Fynex AI, your R&D knowledge assistant. No problems are currently in the system. You can add problems through the Dashboard.`;
    }

    const withSolutions = problemsWithSolutions.filter(p => p.solutions && p.solutions.length > 0).length;

    return `I'm Fynex AI, your R&D knowledge assistant. I have access to ${problems.length} problems in the system (${withSolutions} have solutions).\n\nI can help you analyze:\n• ${problems.filter(p => p.domain === 'mechanical').length} Mechanical problems\n• ${problems.filter(p => p.domain === 'software').length} Software problems\n• ${problems.filter(p => p.domain === 'electronics').length} Electronics problems\n• ${problems.filter(p => p.domain === 'management').length} Management problems\n\nAsk me about specific domains, priorities, solutions, or trends!`;
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || input;
    if (!textToSend.trim()) return;

    if (!hasApiKey) {
      toast.error('Gemini API key not configured');
      return;
    }

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    // Create system prompt with real problem and solution context
    const systemPrompt = `You are Fynex AI, a friendly R&D assistant. You have access to ${problems.length} problems with their solutions: ${JSON.stringify(problemsWithSolutions.slice(0, 3).map(p => ({
      title: p.title,
      description: p.description,
      status: p.status,
      domain: p.domain,
      priority: p.priority,
      solutions: p.solutions.map(s => ({
        description: s.description,
        is_chosen: s.is_chosen
      }))
    })))}. When asked about solutions, refer to the solutions array for each problem. Answer based on this real data.`;

    // For now, simulate AI response delay (replace with real Gemini API call later)
    setTimeout(() => {
      const aiResponse = {
        role: 'assistant',
        content: generateRealDataResponse(textToSend),
        timestamp: new Date().toISOString()
      };

      const finalMessages = [...updatedMessages, aiResponse];
      setMessages(finalMessages);
      setIsLoading(false);

      // Update conversation
      let updated;
      if (activeConversation) {
        updated = conversations.map(c => {
          if (c.id === activeConversation) {
            return {
              ...c,
              messages: finalMessages,
              title: c.messages.length === 0 ? textToSend.slice(0, 30) + '...' : c.title
            };
          }
          return c;
        });
      } else {
        const newConversation = {
          id: Date.now().toString(),
          title: textToSend.slice(0, 30) + '...',
          messages: finalMessages,
          createdAt: new Date().toISOString()
        };
        updated = [newConversation, ...conversations];
        setActiveConversation(newConversation.id);
      }
      setConversations(updated);
      localStorage.setItem('fynex_conversations', JSON.stringify(updated));
    }, 1000);
  };

  const handleQuestionClick = (question) => {
    handleSendMessage(question);
  };

  return (
    <div className="flex h-full" data-testid="ai-assistant-page">
      {/* Left Sidebar - Conversations */}
      <div 
        className={`${sidebarCollapsed ? 'w-[50px]' : 'w-[280px]'} bg-[#1a1f2e] border-r border-white/10 flex flex-col transition-all duration-300 ease-in-out`}
        style={{ minWidth: sidebarCollapsed ? '50px' : '280px' }}
      >
        <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-b border-white/10`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'} mb-4`}>
            {!sidebarCollapsed && <h3 className="font-semibold">Conversations</h3>}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
              </button>
              {!sidebarCollapsed && (
                <button
                  onClick={handleNewChat}
                  className="btn-primary text-sm px-3 py-2 flex items-center gap-1"
                  data-testid="new-chat-btn"
                >
                  <Plus size={16} />
                  New Chat
                </button>
              )}
            </div>
          </div>
        </div>

        {!sidebarCollapsed && (
          <>
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2" data-testid="conversation-list">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-colors ${activeConversation === conv.id
                    ? 'bg-blue-500/10 border-2 border-blue-500'
                    : 'glass-card hover:bg-white/10'
                    }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="text-sm font-medium mb-1 line-clamp-2">{conv.title}</div>
                  <div className="text-xs text-gray-400">
                    {new Date(conv.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>

            {conversations.length > 0 && (
              <div className="p-4 border-t border-white/10">
                <button
                  onClick={handleClearAll}
                  className="w-full btn-secondary text-sm flex items-center justify-center gap-2"
                  data-testid="clear-all-btn"
                >
                  <Trash2 size={16} />
                  Clear All
                </button>
              </div>
            )}
          </>
        )}

        {sidebarCollapsed && (
          <div className="flex-1 flex flex-col items-center justify-center p-2">
            <button
              onClick={handleNewChat}
              className="w-10 h-10 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center mb-4 transition-colors"
              title="New Chat"
              data-testid="new-chat-btn-collapsed"
            >
              <Plus size={20} />
            </button>
            {conversations.length > 0 && (
              <button
                onClick={handleClearAll}
                className="w-10 h-10 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center justify-center transition-colors"
                title="Clear All"
                data-testid="clear-all-btn-collapsed"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right - Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="p-6 border-b border-white/10 bg-[#1a1f2e]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white lg:hidden"
                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <Menu size={20} />
              </button>
              <div>
                <h2 className="text-xl font-bold mb-1" data-testid="chat-title">Fynex AI Assistant</h2>
                <p className="text-sm text-gray-400 flex items-center gap-2">
                  <Sparkles size={14} className="text-purple-400" />
                  Your intelligent R&D companion
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                className="btn-secondary text-sm px-3 py-2 flex items-center gap-2"
                disabled={messages.length === 0}
                data-testid="export-btn"
              >
                <Download size={16} />
                Export
              </button>
              <button
                onClick={handleClearChat}
                className="btn-secondary text-sm px-3 py-2 flex items-center gap-2"
                disabled={messages.length === 0}
                data-testid="clear-chat-btn"
              >
                <Trash2 size={16} />
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-6" data-testid="messages-area">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="max-w-3xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={32} />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">How can I help you today?</h3>
                  <p className="text-gray-400">Ask me anything about your R&D problems</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getSuggestedQuestions().map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuestionClick(question)}
                      className="glass-card p-4 text-left hover:bg-white/10 transition-colors"
                      disabled={!hasApiKey}
                      data-testid={`suggested-question-${index}`}
                    >
                      <p className="text-sm">{question}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  data-testid={`message-${index}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Sparkles size={20} />
                    </div>
                  )}
                  <div
                    className={`max-w-[70%] p-4 rounded-2xl ${message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'glass-card'
                      }`}
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-4" data-testid="loading-indicator">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles size={20} />
                  </div>
                  <div className="glass-card p-4 rounded-2xl">
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="p-6 border-t border-white/10 bg-[#1a1f2e]">
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-2 flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={hasApiKey ? "Ask me anything about your R&D problems..." : "Gemini API key not configured..."}
                className="flex-1 bg-transparent border-none outline-none resize-none text-sm px-2 py-2"
                rows="1"
                style={{ maxHeight: '120px' }}
                disabled={!hasApiKey}
                data-testid="chat-input"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!input.trim() || isLoading || !hasApiKey}
                className="w-10 h-10 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="send-btn"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
